import { Injectable, inject, signal } from '@angular/core';

import { PresenceApiService } from './presence-api.service';
import { AgentPresenceStatus, PresenceHubService } from './presence-hub.service';

/** Single source of truth for "is agent X online right now", combining the REST snapshot
 * (initial page load) with PresenceHubService's live broadcasts. Reusable anywhere in the
 * UI that shows an agent's name (conversation owner, ticket assignee, a future team
 * roster) -- channel-agnostic by design, same as the subsystem it wraps. */
@Injectable({ providedIn: 'root' })
export class AgentPresenceService {
  private readonly api = inject(PresenceApiService);
  private readonly hub = inject(PresenceHubService);

  private readonly statusByUserId = signal<Map<string, AgentPresenceStatus>>(new Map());
  private initialized = false;

  constructor() {
    this.hub.agentPresenceChanged$.subscribe((event) => {
      this.statusByUserId.update((current) => {
        const next = new Map(current);
        next.set(event.userId, event.status);
        return next;
      });
    });
  }

  /** Call once (e.g. from the layout shell) after PresenceHubService.start(). Safe to call
   * more than once -- only the first call actually fetches the snapshot. */
  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.api.getOrganizationPresence().subscribe((rows) => {
      this.statusByUserId.update((current) => {
        const next = new Map(current);
        for (const row of rows) {
          next.set(row.userId, row.status);
        }
        return next;
      });
    });
  }

  /** Template-friendly lookup -- 'Offline' until proven otherwise (never seen a presence
   * row for this user yet), matching the backend's own "lazily created, defaults Offline"
   * convention. */
  statusFor(userId: string | null | undefined): AgentPresenceStatus {
    return userId ? (this.statusByUserId().get(userId) ?? 'Offline') : 'Offline';
  }
}
